import { reactive } from 'vue'
import { apiJSON, BASE_URL, jsonBody } from './http'
import { sessionEpoch } from './session'

export const modelCatalog = reactive({ models: [], loaded: false, error: '', uploadEnabled: false, privateLimit: 50 })
export const MAX_MODEL_BYTES = 20 * 1024 * 1024
export const uploadedModelUrl = (id, revision = '') => `${BASE_URL}/api/models/${encodeURIComponent(id)}/file${revision ? `?revision=${encodeURIComponent(revision)}` : ''}`
export const getUploadedModel = (id) => modelCatalog.models.find((model) => model.id === id)

export function clearModelCatalog() {
  modelCatalog.models = []; modelCatalog.loaded = false; modelCatalog.error = ''; modelCatalog.uploadEnabled = false; modelCatalog.privateLimit = 50
}

export async function refreshModelCatalog() {
  const epoch = sessionEpoch()
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), 15000)
  try {
    const data = await apiJSON('/api/models', { signal: controller.signal })
    modelCatalog.models = (data.models || []).filter((model) => /^custom-[a-f0-9]{32}$/.test(model.id))
    modelCatalog.uploadEnabled = data.uploadEnabled
    modelCatalog.privateLimit = data.quota?.private_limit || 50
    modelCatalog.loaded = true
    modelCatalog.error = ''
  } catch (error) {
    if (epoch === sessionEpoch()) modelCatalog.error = error.name === 'AbortError' ? '模型服务连接超时' : error.message
    throw error
  } finally { clearTimeout(timeout) }
}

export async function uploadModel(file, fields) {
  const query = new URLSearchParams({ name: fields.name, category: fields.category, device_type: fields.deviceType, visibility: fields.visibility || 'private' })
  const model = await apiJSON(`/api/models?${query}`, {
    method: 'POST', headers: { 'Content-Type': 'model/gltf-binary' }, body: file
  })
  modelCatalog.models = [model, ...modelCatalog.models.filter((entry) => entry.id !== model.id)]
  return model
}

function remember(model) {
  modelCatalog.models = modelCatalog.models.map(entry => entry.id === model.id ? model : entry)
  return model
}

export async function updateModel(model, fields) {
  return remember(await apiJSON(`/api/models/${encodeURIComponent(model.id)}`, jsonBody('PATCH', {
    name: fields.name, category: fields.category, device_type: fields.deviceType, expected_version: model.version
  })))
}

export async function replaceModel(model, file) {
  return remember(await apiJSON(`/api/models/${encodeURIComponent(model.id)}/file?expected_version=${model.version}`, {
    method: 'PUT', headers: { 'Content-Type': 'model/gltf-binary' }, body: file
  }))
}

export async function deleteModel(model) {
  await apiJSON(`/api/models/${encodeURIComponent(model.id)}?expected_version=${model.version}`, { method: 'DELETE' })
  modelCatalog.models = modelCatalog.models.filter(entry => entry.id !== model.id)
}

// Inspect before GLTFLoader runs, so local previews cannot request external files.
export function inspectGLB(buffer) {
  if (buffer.byteLength < 20 || buffer.byteLength > MAX_MODEL_BYTES) throw new Error('请选择不超过 20 MiB 的 GLB 文件。')
  const view = new DataView(buffer)
  if (view.getUint32(0, true) !== 0x46546c67 || view.getUint32(4, true) !== 2 || view.getUint32(8, true) !== buffer.byteLength) throw new Error('文件不是有效的 GLB 2.0。')
  const length = view.getUint32(12, true)
  if (view.getUint32(16, true) !== 0x4e4f534a || length % 4 || length + 20 > buffer.byteLength) throw new Error('GLB JSON 分块无效。')
  const document = JSON.parse(new TextDecoder().decode(buffer.slice(20, 20 + length)))
  const check = (value) => {
    if (!value || typeof value !== 'object') return
    Object.entries(value).forEach(([key, child]) => {
      if (['uri', 'url'].includes(key.toLowerCase())) throw new Error('请将贴图和缓冲区嵌入 GLB，不支持外部资源。')
      check(child)
    })
  }
  check(document)
  if (document.extensionsRequired?.length) throw new Error('请导出标准、未压缩且不依赖必需扩展的 GLB。')
  if (!document.meshes?.length || !document.scenes?.length) throw new Error('文件中没有可显示的模型场景。')
  return document
}

